import { useEffect, useState } from "react";
import { X, Download, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Full-screen PDF viewer with a clearly visible close button.
 * Replaces the `<a target="_blank">` pattern that left users stuck on a blank tab
 * (particularly painful inside the installed PWA, which has no tab bar to escape from).
 * Downloading fetches the PDF as a blob and saves it directly — no new tab/window opens.
 *
 * Props:
 *  - src: URL of the PDF
 *  - title: short label (e.g. "Reçu SL-XXXX")
 *  - onClose: callback when the user dismisses
 *  - downloadName: optional download filename
 */
export default function PdfViewerModal({ src, title = "Reçu PDF", onClose, downloadName }) {
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const handleDownload = async () => {
    if (!src || downloading) return;
    setDownloading(true);
    try {
      const res = await api.get(src, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName || "recu.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (_e) {
      // noop — stays on the in-app preview, nothing opens a stray tab either way
    }
    setDownloading(false);
  };

  if (!src) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex flex-col"
      role="dialog"
      aria-modal="true"
      data-testid="pdf-viewer"
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 bg-black/70 backdrop-blur-sm border-b border-white/10">
        <div className="text-white text-sm font-medium truncate min-w-0 flex-1">{title}</div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-3 py-2 transition disabled:opacity-60"
            data-testid="pdf-download"
            title="Télécharger"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            <span className="hidden sm:inline">{downloading ? "Téléchargement..." : "Télécharger"}</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-gray-900 text-xs font-semibold px-3 py-2 shadow-lg hover:bg-gray-100 transition"
            aria-label="Fermer"
            data-testid="pdf-close"
          >
            <X size={16} /> Fermer
          </button>
        </div>
      </div>

      {/* PDF iframe */}
      <div className="flex-1 bg-gray-900">
        <iframe
          src={src}
          title={title}
          className="w-full h-full"
          style={{ border: "none" }}
          data-testid="pdf-iframe"
        />
      </div>

      <div className="text-[11px] text-white/60 text-center py-1.5 select-none bg-black/60">
        Appuyez sur <kbd className="px-1 bg-white/10 rounded text-white">Échap</kbd> ou utilisez le bouton « Fermer » en haut à droite
      </div>
    </div>
  );
}
